import React, { useState, useEffect } from 'react';
import { ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import AsyncStorage from '../AsyncStorageShim';
import ListsManager from '../services/ListsManager';
import { FaArrowLeft, FaCheck } from 'react-icons/fa';
import { useTheme } from '../theme/ThemeContext';

const languages = [
    { name: "🇿🇦 Afrikaans", code: "af" },
    { name: "🇦🇱 Albanian", code: "sq" },
    { name: "🇸🇦 Arabic", code: "ar" },
    { name: "🇦🇲 Armenian", code: "hy" },
    { name: "🇧🇩 Bengali", code: "bn" },
    { name: "🇧🇦 Bosnian", code: "bs" },
    { name: "🇧🇬 Bulgarian", code: "bg" },
    { name: "🇪🇸 Catalan", code: "ca" },
    { name: "🇨🇳 Chinese (Simplified)", code: "zh-CN" },
    { name: "🇹🇼 Chinese (Traditional)", code: "zh-TW" },
    { name: "🇭🇷 Croatian", code: "hr" },
    { name: "🇨🇿 Czech", code: "cs" },
    { name: "🇩🇰 Danish", code: "da" },
    { name: "🇳🇱 Dutch", code: "nl" },
    { name: "🇺🇸 English", code: "en" },
    { name: "🇪🇺 Esperanto", code: "eo" },
    { name: "🇪🇪 Estonian", code: "et" },
    { name: "🇵🇭 Filipino", code: "tl" },
    { name: "🇫🇮 Finnish", code: "fi" },
    { name: "🇫🇷 French", code: "fr" },
    { name: "🇬🇪 Georgian", code: "ka" },
    { name: "🇩🇪 German", code: "de" },
    { name: "🇬🇷 Greek", code: "el" },
    { name: "🇮🇳 Gujarati", code: "gu" },
    { name: "🇭🇹 Haitian Creole", code: "ht" },
    { name: "🇮🇱 Hebrew", code: "he" },
    { name: "🇮🇳 Hindi", code: "hi" },
    { name: "🇭🇺 Hungarian", code: "hu" },
    { name: "🇮🇸 Icelandic", code: "is" },
    { name: "🇮🇩 Indonesian", code: "id" },
    { name: "🇮🇪 Irish", code: "ga" },
    { name: "🇮🇹 Italian", code: "it" },
    { name: "🇯🇵 Japanese", code: "ja" },
    { name: "🇮🇳 Kannada", code: "kn" },
    { name: "🇰🇿 Kazakh", code: "kk" },
    { name: "🇰🇷 Korean", code: "ko" },
    { name: "🇱🇦 Lao", code: "lo" },
    { name: "🇱🇻 Latvian", code: "lv" },
    { name: "🇱🇹 Lithuanian", code: "lt" },
    { name: "🇲🇰 Macedonian", code: "mk" },
    { name: "🇲🇾 Malay", code: "ms" },
    { name: "🇲🇹 Maltese", code: "mt" },
    { name: "🇮🇳 Marathi", code: "mr" },
    { name: "🇲🇳 Mongolian", code: "mn" },
    { name: "🇳🇵 Nepali", code: "ne" },
    { name: "🇳🇴 Norwegian", code: "no" },
    { name: "🇮🇷 Persian", code: "fa" },
    { name: "🇵🇱 Polish", code: "pl" },
    { name: "🇵🇹 Portuguese", code: "pt" },
    { name: "🇮🇳 Punjabi", code: "pa" },
    { name: "🇷🇴 Romanian", code: "ro" },
    { name: "🇷🇺 Russian", code: "ru" },
    { name: "🇷🇸 Serbian", code: "sr" },
    { name: "🇱🇰 Sinhala", code: "si" },
    { name: "🇸🇰 Slovak", code: "sk" },
    { name: "🇸🇮 Slovenian", code: "sl" },
    { name: "🇪🇸 Spanish", code: "es" },
    { name: "🇰🇪 Swahili", code: "sw" },
    { name: "🇸🇪 Swedish", code: "sv" },
    { name: "🇮🇳 Tamil", code: "ta" },
    { name: "🇮🇳 Telugu", code: "te" },
    { name: "🇹🇭 Thai", code: "th" },
    { name: "🇹🇷 Turkish", code: "tr" },
    { name: "🇺🇦 Ukrainian", code: "uk" },
    { name: "🇵🇰 Urdu", code: "ur" },
    { name: "🇺🇿 Uzbek", code: "uz" },
    { name: "🇻🇳 Vietnamese", code: "vi" },
    { name: "🏴󠁧󠁢󠁷󠁬󠁳󠁿 Welsh", code: "cy" },
    { name: "🇿🇦 Xhosa", code: "xh" },
    { name: "🇿🇦 Zulu", code: "zu" },
];

const LanguagesScreen = ({ onBack } = {}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { theme } = useTheme();
    const isFromTranslation = location.state?.isFromTranslation ?? false;
    const embedded = !!onBack;

    const T = {
        bg:        theme.bg,
        card:      theme.bgCard,
        surface:   theme.bgSurface,
        border:    theme.border,
        text:      theme.textPrimary,
        textSec:   theme.textSecondary,
        textMuted: theme.textMuted,
        primary:   '#0a84ff',
    };

    const [searchText, setSearchText]               = useState('');
    const [filteredLanguages, setFilteredLanguages] = useState(languages);
    const [selectedLanguageCode, setSelected]       = useState(null);
    const [saving, setSaving]                       = useState(false);
    const [error, setError]                         = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                if (isFromTranslation) {
                    const code = await AsyncStorage.getItem('Translation_Language');
                    if (code) setSelected(code);
                } else {
                    const lang = await ListsManager.getListenerLanguage();
                    if (lang) setSelected(lang);
                }
            } catch (e) {
                console.error('Error loading language:', e);
            }
        };
        load();
    }, [isFromTranslation]);

    const handleSelect = async (language) => {
        try {
            setSaving(true);
            setSelected(language.code);
            if (isFromTranslation) {
                await AsyncStorage.setItem('Translation_Language', language.code);
            } else {
                const ok = await ListsManager.updateListenerLanguage(language.code);
                if (!ok) throw new Error('Failed to update language preference');
            }
            setSaving(false);
            if (!embedded) {
                if (onBack) onBack(); else navigate(-1);
            }
        } catch (e) {
            setError(e.message || 'Error saving language');
            setSaving(false);
        }
    };

    const handleSearch = (text) => {
        setSearchText(text);
        setFilteredLanguages(
            text.trim()
                ? languages.filter(l => l.name.toLowerCase().includes(text.toLowerCase()))
                : languages
        );
    };

    if (saving) {
        return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 }}>
                <ActivityIndicator size="large" color={T.primary} />
                <span style={{ color: T.textSec, fontSize: 14 }}>{t('app.languages.updating')}</span>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg, overflow: 'hidden' }}>
            {/* Header */}
            {!embedded ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '60px 20px 16px', borderBottom: `1px solid ${T.border}`, background: T.card, flexShrink: 0 }}>
                    <button onClick={() => navigate(-1)} style={{ padding: 10, borderRadius: 12, background: T.surface, border: 'none', cursor: 'pointer' }}>
                        <FaArrowLeft color={T.text} size={16} />
                    </button>
                    <span style={{ color: T.text, fontSize: 20, fontWeight: 700 }}>{t('app.languages.title')}</span>
                    <div style={{ width: 40 }} />
                </div>
            ) : (
                <div style={{ padding: '20px 28px 14px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
                    <span style={{ color: T.text, fontSize: 16, fontWeight: 700 }}>{t('app.languages.title')}</span>
                    <div style={{ color: T.textMuted, fontSize: 12, marginTop: 2 }}>{t('app.languages.subtitle')}</div>
                </div>
            )}

            {/* Search */}
            <div style={{ padding: embedded ? '12px 28px' : '12px 16px', flexShrink: 0 }}>
                <input
                    value={searchText}
                    onChange={e => handleSearch(e.target.value)}
                    placeholder={t('app.languages.search')}
                    style={{
                        width: '100%', boxSizing: 'border-box',
                        height: 40, borderRadius: 10, padding: '0 14px',
                        background: T.surface, border: `1px solid ${T.border}`,
                        color: T.text, fontSize: 14, outline: 'none', fontFamily: 'inherit',
                    }}
                />
            </div>

            {error && (
                <div style={{ margin: '0 28px 12px', padding: '10px 14px', borderRadius: 10, background: 'rgba(255,69,58,0.08)', border: '1px solid rgba(255,69,58,0.25)', color: '#ff453a', fontSize: 12 }}>
                    {error}
                </div>
            )}

            {/* Grid */}
            <div style={{ flex: 1, overflowY: 'auto', padding: embedded ? '12px 28px 28px' : '12px 16px 28px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 }}>
                    {filteredLanguages.map((lang) => {
                        const isSelected = lang.code === selectedLanguageCode;
                        const [flag, ...nameParts] = lang.name.split(' ');
                        const name = nameParts.join(' ');
                        return (
                            <div
                                key={lang.code}
                                onClick={() => handleSelect(lang)}
                                style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                    padding: '14px 8px', borderRadius: 12, cursor: 'pointer',
                                    background: isSelected ? `${T.primary}14` : T.card,
                                    border: `1px solid ${isSelected ? T.primary : T.border}`,
                                    transition: 'all 0.12s', gap: 6,
                                    boxShadow: isSelected ? `0 0 0 1px ${T.primary}40` : 'none',
                                }}
                                onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.background = T.surface; e.currentTarget.style.borderColor = T.primary + '55'; } }}
                                onMouseLeave={e => { e.currentTarget.style.background = isSelected ? `${T.primary}14` : T.card; e.currentTarget.style.borderColor = isSelected ? T.primary : T.border; }}
                            >
                                <span style={{ fontSize: 22, lineHeight: 1 }}>{flag}</span>
                                <span style={{
                                    color: isSelected ? T.primary : T.text,
                                    fontSize: 11, fontWeight: isSelected ? 700 : 500,
                                    textAlign: 'center', lineHeight: '14px',
                                    wordBreak: 'break-word',
                                }}>
                                    {name}
                                </span>
                                {isSelected && (
                                    <div style={{ width: 6, height: 6, borderRadius: 3, background: T.primary }} />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default LanguagesScreen;
